import { Component, inject, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { ExperimentService } from '@services/experiment';
import { ApiClientConstant } from '@cureskin/api-client/src/constant/api-client.constant';
import { Router } from '@angular/router';
import { Catalog, ReorderKitDetail } from '@cureskin/api-client/src/server-api';
import { take } from 'rxjs/operators';
import { EventLoggerService } from '@services/event-logger-service';
import { FOLLOWUP_NUDGE_TYPE, FollowUpNudgeType } from '@services/regimen-reorder-service/regimen-reorder.types';
import { FollowUpNudgeBottomsheetComponent } from '../bottom-sheet-layouts/followup-nudge-bottomsheet/followup-nudge-bottomsheet.component';

@Component({
  selector: 'regimen-reorder-banner',
  imports: [ButtonCtaComponent, DatePipe],
  templateUrl: './regimen-reorder-banner.component.html',
})
export class RegimenReorderBannerComponent {

  private readonly experimentService = inject<ExperimentService>(ExperimentService);
  private readonly router = inject<Router>(Router);
  private readonly eventLoggerService = inject<EventLoggerService>(EventLoggerService);
  private readonly bottomSheet = inject(MatBottomSheet);
  readonly ApiClientConstant = ApiClientConstant;
  private isBottomSheetOpen = false;

  bannerDetail = input<ReorderKitDetail>();
  orderState = input<string>();

  ngOnInit(): void {
    this.eventLoggerService.cleverTapEvent('impression', JSON.stringify({
      name: 'regimen_reorder_banner',
      totalMrp: this.bannerDetail()?.totalMrp,
      totalSP: this.bannerDetail()?.totalSP,
      discountInRupees: this.bannerDetail()?.discountInRupees,
      regimenClass: this.bannerDetail()?.regimenClass
    }));
  }

  private get daysSinceUpdate(): number {
    if (!this.bannerDetail()?.lastUpdatedAt) return -1;
    const diffMs = Date.now() - new Date(this.bannerDetail()!.lastUpdatedAt).getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  get isUpdatedToday(): boolean {
    return this.daysSinceUpdate === 0;
  }

  get isUpdatedYesterday(): boolean {
    return this.daysSinceUpdate === 1;
  }

  get isUpdatedBeforeYesterday(): boolean {
    return !this.isUpdatedToday && !this.isUpdatedYesterday;
  }

  get lastUpdatedAtDate(): Date | null {
    return this.bannerDetail()?.lastUpdatedAt
      ? new Date(this.bannerDetail()!.lastUpdatedAt)
      : null;
  }

  onBuyNow(): void {
    const detail = this.bannerDetail();
    const productIds = detail?.products.map((product: Catalog) => product.objectId);
    const productIdsString = productIds.toString();
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({
      name: 'regimen_reorder_banner_buy_now',
      totalMrp: detail?.totalMrp,
      totalSP: detail?.totalSP,
      discountInRupees: detail?.discountInRupees,
      regimenClass: detail?.regimenClass
    }));

    const nudgeType = this.getNudgeType(detail);
    if (nudgeType) {
      this.openFollowUpNudgeBottomSheet(nudgeType, productIdsString);
      return;
    }

    this.navigateToCheckout(productIdsString);
  }

  private getNudgeType(detail: ReorderKitDetail): FollowUpNudgeType | null {
    if (detail?.isNoFollowUpTaken) return FOLLOWUP_NUDGE_TYPE.NO_FOLLOWUP;
    if (detail?.isFollowUpOverdue) return FOLLOWUP_NUDGE_TYPE.OVERDUE;
    if (detail?.isFollowUpUpcoming) return FOLLOWUP_NUDGE_TYPE.UPCOMING;
    return null;
  }

  private openFollowUpNudgeBottomSheet(type: FollowUpNudgeType, productIdsString: string): void {
    if (this.isBottomSheetOpen) return;
    this.isBottomSheetOpen = true;

    const detail = this.bannerDetail();
    const ref = this.bottomSheet.open(FollowUpNudgeBottomsheetComponent, {
      data: {
        type,
        followUpId: detail?.followUpId,
        followUpDate: detail?.followUpDate,
        lastUpdatedAt: detail?.lastUpdatedAt,
        regimenClass: detail?.regimenClass,
      },
    });

    ref.afterDismissed().pipe(take(1)).subscribe((result?: { navigateToCheckup: boolean }) => {
      this.isBottomSheetOpen = false;
      if (!result?.navigateToCheckup) {
        this.navigateToCheckout(productIdsString);
      }
    });
  }

  navigateToCheckout(productIdsString: string): void {
    this.router.navigate(['/checkout'], {
      queryParams: {
        products: productIdsString,
        type: ApiClientConstant.Order.Type.PRODUCT,
        useCureSkinCash: String(this.experimentService.shouldUseCureSkinCash(this.orderState())),
        isRegimenReorder: true.toString(),
        regimenClass: this.bannerDetail()?.regimenClass
      }
    });
  }

  get bannerKitImage(): string {
    return this.bannerDetail()?.regimenClass === ApiClientConstant.Regimen.Class.HAIR
      ? 'https://cdn.cureskin.com/app/img/dr_holding_purple_kit.png'
      : 'https://cdn.cureskin.com/app/img/dr_holding_orange_kit.png';
  }

  get isHairRegimen(): boolean {
    return this.bannerDetail()?.regimenClass === ApiClientConstant.Regimen.Class.HAIR;
  }

  get doctorFirstName(): string {
    return this.bannerDetail()?.lastUpdatedBy?.split(' ').slice(0, 2).join(' ') || '';
  }
}
